<template>
  <div
    v-if="hasMultipleOrganizations"
    ref="switcherRef"
    class="org-switcher"
    :class="{ 'org-switcher--collapsed': collapsed }"
    data-testid="org-switcher"
  >
    <button
      class="org-switcher__trigger"
      data-testid="org-switcher-trigger"
      :disabled="isSwitching"
      :title="collapsed ? activeOrganization?.name : undefined"
      @click="toggleDropdown"
    >
      <Building2 class="org-switcher__icon" :size="20" />
      <span class="org-switcher__name" data-testid="org-switcher-current">{{
        activeOrganization?.name
      }}</span>
      <ChevronDown
        class="org-switcher__chevron"
        :class="{ 'org-switcher__chevron--open': isOpen }"
        :size="16"
      />
    </button>

    <Transition name="dropdown">
      <div v-if="isOpen" class="org-switcher__dropdown" data-testid="org-switcher-dropdown">
        <button
          v-for="org in organizations"
          :key="org.id"
          class="org-switcher__item"
          :class="{ 'org-switcher__item--active': org.id === activeOrganization?.id }"
          :data-testid="`org-switcher-option-${org.id}`"
          :disabled="isSwitching"
          @click="handleSwitch(org.id)"
        >
          <Check
            v-if="org.id === activeOrganization?.id"
            class="org-switcher__check"
            :size="16"
          />
          <span
            class="org-switcher__item-name"
            :class="{ 'org-switcher__item-name--with-check': org.id === activeOrganization?.id }"
          >
            {{ org.name }}
          </span>
        </button>
      </div>
    </Transition>

    <div v-if="isSwitching" class="org-switcher__loading">
      <span class="org-switcher__spinner"></span>
      {{ t('organization.switching') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useOrganization } from '@/composables/useOrganization'
import { useAuthStore } from '@/stores/auth'
import { Building2, ChevronDown, Check } from 'lucide-vue-next'

withDefaults(
  defineProps<{
    /** Render the icon-only rail variant for the collapsed sidebar. */
    collapsed?: boolean
  }>(),
  { collapsed: false }
)

const { t } = useI18n()
const authStore = useAuthStore()

const {
  organizations,
  activeOrganization,
  hasMultipleOrganizations,
  isSwitching,
  fetchOrganizations,
  switchOrganization,
} = useOrganization()

const isOpen = ref(false)
const switcherRef = ref<HTMLElement | null>(null)

function toggleDropdown() {
  if (!isSwitching.value) {
    isOpen.value = !isOpen.value
  }
}

function handleClickOutside(event: MouseEvent) {
  if (switcherRef.value && !switcherRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

async function handleSwitch(orgId: string) {
  if (orgId !== activeOrganization.value?.id) {
    isOpen.value = false
    await switchOrganization(orgId)
  }
}

onMounted(() => {
  // Only fetch if user is authenticated - otherwise the composable will return early
  // and we'll fetch when auth state changes (Fixes SCRIVIO-FE-2S)
  if (authStore.isAuthenticated) {
    fetchOrganizations()
  }
  document.addEventListener('click', handleClickOutside)
})

// Watch for auth state changes and fetch organizations when user becomes authenticated
watch(
  () => authStore.isAuthenticated,
  (isAuthenticated) => {
    if (isAuthenticated && organizations.value.length === 0) {
      fetchOrganizations()
    }
  }
)

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.org-switcher {
  position: relative;
  width: 100%;
}

.org-switcher__trigger {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  width: 100%;
  padding: var(--spacing-md) var(--spacing-lg);
  border-radius: var(--radius-md);
  background: var(--color-overlay-light);
  border: 1px solid var(--color-overlay-on-dark);
  color: var(--color-text-inverse);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-duration-normal) var(--transition-easing-ease-out);
  text-align: left;
}

.org-switcher__trigger:hover:not(:disabled) {
  background: var(--color-overlay-on-dark);
  border-color: var(--color-overlay-on-dark-strong);
}

.org-switcher__trigger:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.org-switcher__icon {
  flex-shrink: 0;
}

/* Collapsed icon-rail: icon-only trigger that still opens the dropdown.
   The org name stays in the DOM (sr-only) so it remains accessible and the
   :title attribute supplies a hover tooltip. */
.org-switcher--collapsed .org-switcher__trigger {
  justify-content: center;
  gap: 0;
  padding: var(--spacing-md);
}

.org-switcher--collapsed .org-switcher__name {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.org-switcher--collapsed .org-switcher__chevron {
  display: none;
}

.org-switcher__name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.org-switcher__chevron {
  flex-shrink: 0;
  transition: transform var(--transition-duration-fast) var(--transition-easing-ease-out);
}

.org-switcher__chevron--open {
  transform: rotate(180deg);
}

.org-switcher__dropdown {
  position: absolute;
  top: calc(100% + var(--spacing-xs));
  left: 0;
  min-width: 240px;
  max-width: 320px;
  z-index: var(--z-dropdown);
  background: var(--color-background-content);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}

.org-switcher__item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: var(--spacing-md) var(--spacing-lg);
  background: transparent;
  border: none;
  color: var(--color-text-primary);
  font-size: var(--font-size-base);
  text-align: left;
  cursor: pointer;
  transition: background var(--transition-duration-fast) var(--transition-easing-ease-out);
}

.org-switcher__item:hover:not(:disabled) {
  background: var(--color-background-canvas);
}

.org-switcher__item:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.org-switcher__item--active {
  background: var(--color-background-secondary);
  font-weight: var(--font-weight-medium);
}

.org-switcher__check {
  flex-shrink: 0;
  color: var(--color-brand-primary);
  margin-right: var(--spacing-sm);
}

.org-switcher__item-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.org-switcher__item-name--with-check {
  /* Item with check doesn't need extra margin */
}

.org-switcher__item:not(.org-switcher__item--active) .org-switcher__item-name {
  margin-left: calc(16px + var(--spacing-sm));
}

.org-switcher__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  color: var(--color-overlay-on-dark-text);
  font-size: var(--font-size-sm);
}

.org-switcher--collapsed .org-switcher__loading {
  padding: var(--spacing-sm) 0;
}

.org-switcher__spinner {
  width: 14px;
  height: 14px;
  border: 2px solid var(--color-overlay-on-dark-strong);
  border-top-color: var(--color-text-inverse);
  border-radius: 50%;
  animation: org-switcher-spin 0.6s linear infinite;
}

@keyframes org-switcher-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Dropdown animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all var(--transition-duration-fast) var(--transition-easing-ease-out);
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* On tablet/mobile the sidebar is always rendered expanded (SideMenu disables
   collapsed mode below 1024px), so the icon-only rail variant falls back to the
   full trigger to stay consistent with the labelled nav items. */
@media (--breakpoint-lg-down) {
  .org-switcher--collapsed .org-switcher__trigger {
    justify-content: flex-start;
    gap: var(--spacing-md);
    padding: var(--spacing-md) var(--spacing-lg);
  }

  .org-switcher--collapsed .org-switcher__name {
    position: static;
    width: auto;
    height: auto;
    margin: 0;
    clip: auto;
  }

  .org-switcher--collapsed .org-switcher__chevron {
    display: block;
  }
}

@media (--breakpoint-xxs-down) {
  .org-switcher__trigger {
    padding: var(--spacing-sm) var(--spacing-md);
    gap: var(--spacing-sm);
    font-size: var(--font-size-sm);
  }

  .org-switcher__item {
    padding: var(--spacing-sm) var(--spacing-md);
    font-size: var(--font-size-sm);
  }
}
</style>
